import React, { useState, useEffect } from 'react';
import ModalOverlay from '../../shared/ModalOverlay';
import { DistroList, DistroEmail, POC } from '../../types';

interface DistroEditModalProps {
  active: boolean;
  onClose: () => void;
  onSave: (data: { name: string; emails: DistroEmail[] }) => void;
  distro?: DistroList | null;
  pocs: POC[];
}

/** Split a POC's single name field into first/last for a distro email entry. */
function pocToEmail(poc: POC): DistroEmail {
  const parts = (poc.poc_name || '').trim().split(/\s+/);
  const first = parts.shift() || '';
  return { first_name: first, last_name: parts.join(' '), email: poc.email || '' };
}

const DistroEditModal: React.FC<DistroEditModalProps> = ({ active, onClose, onSave, distro, pocs }) => {
  const [name, setName] = useState('');
  const [emails, setEmails] = useState<DistroEmail[]>([]);
  const [pocPickerOpen, setPocPickerOpen] = useState(false);
  const [pocSearch, setPocSearch] = useState('');

  useEffect(() => {
    if (distro) {
      setName(distro.name);
      setEmails(distro.emails || []);
    } else {
      setName('');
      setEmails([]);
    }
  }, [distro, active]);

  const addEmail = () => {
    setEmails([...emails, { first_name: '', last_name: '', email: '' }]);
  };

  const updateEmail = (index: number, field: keyof DistroEmail, value: string) => {
    const updated = [...emails];
    updated[index] = { ...updated[index], [field]: value };
    setEmails(updated);
  };

  const removeEmail = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  const addPoc = (poc: POC) => {
    const entry = pocToEmail(poc);
    if (!entry.email) return;
    // Skip if this email is already on the list.
    if (emails.some((e) => e.email.trim().toLowerCase() === entry.email.trim().toLowerCase())) return;
    setEmails((prev) => [...prev, entry]);
  };

  const handleSubmit = () => {
    onSave({ name, emails: emails.filter((e) => e.email) });
  };

  const existingEmails = new Set(emails.map((e) => e.email.trim().toLowerCase()));
  const query = pocSearch.trim().toLowerCase();
  const filteredPocs = pocs
    .filter((p) => p.email)
    .filter((p) => !query
      || (p.poc_name || '').toLowerCase().includes(query)
      || (p.vendor || '').toLowerCase().includes(query)
      || (p.email || '').toLowerCase().includes(query));

  return (
    <ModalOverlay active={active} onClose={onClose}>
      <div className="modal modal-narrow">
        <div className="modal-header">
          <h2>{distro ? 'Edit Distribution List' : 'New Distribution List'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div style={{ padding: '1rem 1.5rem' }}>
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label>List Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="subtable-header" style={{ marginBottom: '0.5rem' }}>
            <h3 style={{ fontSize: '0.8rem', color: 'var(--text-dim)', textTransform: 'uppercase' }}>Emails</h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setPocSearch(''); setPocPickerOpen(true); }}>+ From POCs</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addEmail}>+ Email</button>
            </div>
          </div>
          {emails.map((entry, i) => (
            <div key={i} className="distro-email-row">
              <input className="distro-fn" placeholder="First Name" value={entry.first_name} onChange={(e) => updateEmail(i, 'first_name', e.target.value)} />
              <input className="distro-ln" placeholder="Last Name" value={entry.last_name} onChange={(e) => updateEmail(i, 'last_name', e.target.value)} />
              <input className="distro-email-input" type="email" placeholder="Email" value={entry.email} onChange={(e) => updateEmail(i, 'email', e.target.value)} />
              <button className="btn-icon delete" onClick={() => removeEmail(i)}>&times;</button>
            </div>
          ))}
        </div>
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>Save</button>
        </div>

        {pocPickerOpen && (
          <div className="modal-overlay active" style={{ zIndex: 120 }} onClick={(e) => { if (e.target === e.currentTarget) setPocPickerOpen(false); }}>
            <div className="modal modal-narrow">
              <div className="modal-header">
                <h2>Add from POCs</h2>
                <button className="modal-close" onClick={() => setPocPickerOpen(false)}>&times;</button>
              </div>
              <div style={{ padding: '0.75rem 1.5rem 0' }}>
                <div className="form-group">
                  <label>Search</label>
                  <input type="text" placeholder="Name, vendor, or email…" value={pocSearch} onChange={(e) => setPocSearch(e.target.value)} />
                </div>
              </div>
              <div style={{ padding: '0.5rem 1.5rem 1rem', maxHeight: '360px', overflowY: 'auto' }}>
                {filteredPocs.length === 0 ? (
                  <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '1rem' }}>No POCs found.</p>
                ) : (
                  filteredPocs.map((p) => {
                    const added = existingEmails.has((p.email || '').trim().toLowerCase());
                    return (
                      <div key={p.id} className="poc-list-item">
                        <div className="poc-list-item-info">
                          <span className="poc-list-item-name">{p.poc_name || p.email}</span>
                          <span className="poc-list-item-vendor">
                            {[p.vendor, p.email].filter(Boolean).join(' · ')}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={added}
                          onClick={() => addPoc(p)}
                        >
                          {added ? 'Added' : 'Add'}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-primary" onClick={() => setPocPickerOpen(false)}>Done</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ModalOverlay>
  );
};

export default DistroEditModal;
